// ============================================
// PRTCL Content Level System
// Adapts app verbiage based on user experience level
// ============================================

export type ContentLevel = 'beginner' | 'intermediate' | 'advanced';

// `disabled: true` greys the option in the picker - users see it
// (preview of what's coming) but can't select it. Only beginner is
// available in the current release.
export const CONTENT_LEVELS: { id: ContentLevel; label: string; description: string; disabled?: boolean }[] = [
  { id: 'beginner', label: 'Beginner', description: 'Plain English, extra explanations' },
  { id: 'intermediate', label: 'Intermediate', description: 'Coming soon', disabled: true },
  { id: 'advanced', label: 'Advanced', description: 'Coming soon', disabled: true },
];

// Map userType from onboarding to content level
export function userTypeToContentLevel(userType?: string): ContentLevel {
  switch (userType) {
    case 'first_timer': return 'beginner';
    case 'experienced': return 'advanced';
    case 'exploring': return 'intermediate';
    default: return 'advanced';
  }
}

// ---- Content Registry ----
// Each key maps to { beginner, intermediate, advanced } text.
// If a level is omitted, it falls back: intermediate → advanced, beginner → intermediate → advanced.

type ContentEntry = {
  beginner?: string;
  intermediate?: string;
  advanced: string; // always required as the base
};

const content: Record<string, ContentEntry> = {
  // ============================================
  // CALCULATOR PAGE
  // ============================================

  // ============================================
  // PROTOCOLS PAGE
  // ============================================

  'protocols.subtitle': {
    beginner: '',  // handled with JSX (has bold formatting)
    advanced: 'Your active peptide protocols',
  },

  'protocols.step0.desc': {
    beginner: 'Enter the details of your peptide and how it was mixed. This information should be on your vial label or provided by your doctor.',
    advanced: '',
  },

  'protocols.step1.desc': {
    beginner: 'How often will you take this peptide? Your doctor should have given you a schedule to follow.',
    advanced: '',
  },

  'protocols.step2.desc': {
    beginner: 'What time of day will you inject? The app will send you a reminder at this time.',
    advanced: '',
  },

  'protocols.step3.desc': {
    beginner: 'Some peptides are taken continuously, others in cycles (e.g. 8 weeks on, 4 weeks off). Check with your doctor if you\'re unsure.',
    advanced: '',
  },

  'protocols.step4.desc': {
    beginner: 'Some peptides work best on an empty stomach while others can be taken anytime. Your doctor may have specific instructions.',
    advanced: '',
  },

  // ============================================
  // CALCULATOR PAGE
  // ============================================

  'calc.subtitle': {
    beginner: 'Figure out exactly how many units to draw in your syringe for each dose. You should have all of this information from your doctor - if anything is missing, contact them before proceeding.',
    advanced: 'Real-time reconstitution & dosing math',
  },

  'calc.disclaimer': {
    beginner:
      "Always use the dose your prescriber gave you. This tool only converts the numbers you enter - it does not recommend a dose.",
    advanced:
      'Consult your prescriber for your dose. This tool converts user-entered values only and does not recommend a dose.',
  },

  'calc.vialSize.helper': {
    beginner: 'This is the amount of peptide in your vial, printed on the label - typically in mg (milligrams).',
    advanced: '',
  },

  'calc.vialSize.belowInput': {
    beginner: 'mg = milligrams · mcg = micrograms · mL = milliliters',
    advanced: '',
  },

  'calc.bacWater.label': {
    advanced: 'Bacteriostatic Water',
  },

  'calc.bacWater.helper': {
    beginner: "Not sure? 2 mL is most common but check with your doctor for the correct amount.",
    intermediate: "Not sure? 2 mL is most common but check with your doctor for the correct amount.",
    advanced: 'Not sure? Check your pharmacy instructions. 2 mL is common.',
  },

  'calc.bacWater.belowLabel': {
    beginner:
      "Sterile water with a preservative added, so more than one dose can be drawn from the same vial. How long a mixed vial can be used is set by your product's instructions.",
    advanced: '',
  },

  'calc.dose.helper': {
    beginner: 'This should have been provided to you by your doctor.',
    advanced: '',
  },


  'calc.syringe.helper': {
    beginner: 'These are the most common sizes for injecting just under the skin. For peptides, U-100 or U-50 usually work best. All three sizes are marked the same. They just hold different amounts.',
    advanced: '',
  },

  'calc.results.heading': {
    beginner: "Here's how much to take in each injection per your doctor's instructions.",
    advanced: 'Results',
  },

  'calc.results.concentration': {
    beginner: 'Amount per injection',
    advanced: 'Concentration',
  },

  'calc.results.concentrationDesc': {
    beginner: 'Each injection will contain this amount of your peptide.',
    advanced: '',
  },

  'calc.results.drawTo': {
    beginner: 'Units to draw on your syringe',
    advanced: 'Draw to',
  },

  'calc.results.drawToDesc': {
    beginner: 'For each dose, fill your syringe to this unit mark.',
    advanced: '',
  },

  'calc.results.dosesPerVial': {
    beginner: 'How long your vial will last',
    advanced: 'Doses per Vial',
  },

  'calc.stack.hint': {
    beginner: 'Enter the amount of each peptide in your combined vial. This information should have been provided by your doctor or may be printed on the vial label.',
    advanced: 'Enter the amount of each peptide in your combined vial',
  },

  'calc.stack.breakdownDesc': {
    beginner: 'Since this vial contains multiple peptides mixed together, this is how much of each peptide you will get per injection.',
    advanced: '',
  },

  // ============================================
  // PEPTIDE / STACK DETAIL PAGE
  // ============================================

  'detail.overview.usedFor.title': {
    beginner: 'What people use it for',
    intermediate: "What it's used for",
    advanced: 'Indications',
  },

  'detail.overview.peptidesInStack.title': {
    beginner: "What's in this stack",
    intermediate: 'Stack components',
    advanced: 'Components',
  },

  'detail.overview.howItWorks.title': {
    beginner: 'How it works',
    intermediate: 'How it works',
    advanced: 'Mechanism',
  },

  'detail.protocols.title': {
    beginner: 'What people usually take',
    intermediate: 'Standard protocol',
    advanced: 'Reference protocol',
  },

  'detail.protocols.timingNote.title': {
    beginner: 'Timing tips',
    intermediate: 'Timing notes',
    advanced: 'Timing considerations',
  },

  'detail.protocols.storage.title': {
    beginner: 'Storage',
    intermediate: 'Storage',
    advanced: 'Storage requirements',
  },

  'detail.safety.sideEffects.title': {
    beginner: 'Common side effects',
    intermediate: 'Side effects',
    advanced: 'Adverse effects',
  },

  'detail.safety.avoid.title': {
    beginner: 'Who should avoid',
    intermediate: 'Contraindications',
    advanced: 'Contraindications',
  },

  'detail.research.trustedVoices.title': {
    beginner: 'Trusted voices',
    intermediate: 'Expert insights',
    advanced: 'Clinician sources',
  },

  'detail.research.goDeeper.title': {
    beginner: 'Go deeper',
    intermediate: 'Deeper reading',
    advanced: 'Reference material',
  },

  'detail.gating.protocols.title': {
    beginner: 'Subscribe to see protocols',
    advanced: 'Subscribe to see protocols',
  },

  'detail.gating.protocols.body': {
    beginner: 'Get the full dosing guide, timing notes, and storage instructions.',
    advanced: 'Full dosing reference, cycle planning, and storage details.',
  },

  'detail.gating.safety.title': {
    beginner: 'Subscribe to see safety details',
    advanced: 'Subscribe to see safety details',
  },

  'detail.gating.safety.body': {
    beginner: 'Side effects, who should avoid this peptide, and our full disclaimer.',
    advanced: 'Adverse effect profile, contraindications, and disclaimer.',
  },

  'detail.gating.research.title': {
    beginner: 'Subscribe to see research',
    advanced: 'Subscribe to see research',
  },

  'detail.gating.research.body': {
    beginner: 'Doctor insights, published research, and detailed mechanism explanations.',
    advanced: 'Practitioner commentary, primary literature, and mechanism detail.',
  },
};

// ---- Accessor ----

let currentLevel: ContentLevel = 'advanced';

export function setContentLevel(level: ContentLevel) {
  currentLevel = level;
}

export function getContentLevel(): ContentLevel {
  return currentLevel;
}

/**
 * Get content string for the current level.
 * Falls back: beginner → intermediate → advanced
 */
export function t(key: string): string {
  const entry = content[key];
  if (!entry) return ''; // return empty for undefined keys

  if (currentLevel === 'beginner') {
    return entry.beginner ?? entry.intermediate ?? entry.advanced;
  }
  if (currentLevel === 'intermediate') {
    return entry.intermediate ?? entry.advanced;
  }
  return entry.advanced;
}
